import React, { useMemo } from 'react';
import styled from 'styled-components';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { axiomTheme } from '../../styles/designTokens';

interface InstitutionalChartProps {
  options: Highcharts.Options;
  height?: number;
}

const ChartShell = styled.div<{ $height: number }>`
  height: ${p => p.$height}px;

  .highcharts-background {
    fill: transparent;
  }

  .highcharts-title,
  .highcharts-subtitle {
    fill: ${axiomTheme.color.text.primary} !important;
  }

  .highcharts-axis-labels text,
  .highcharts-legend-item text,
  .highcharts-axis-title {
    fill: ${axiomTheme.color.text.muted} !important;
    color: ${axiomTheme.color.text.muted} !important;
  }

  .highcharts-grid-line {
    stroke: rgba(148, 163, 184, 0.12);
  }

  .highcharts-tooltip {
    filter: drop-shadow(0 16px 28px rgba(2, 6, 23, 0.42));
  }
`;

const baseOptions: Highcharts.Options = {
  chart: {
    backgroundColor: 'transparent',
    style: {
      fontFamily: 'inherit',
    },
  },
  credits: {
    enabled: false,
  },
  title: {
    style: {
      color: axiomTheme.color.text.primary,
      fontWeight: '800',
    },
  },
  subtitle: {
    style: {
      color: axiomTheme.color.text.muted,
    },
  },
  xAxis: {
    lineColor: axiomTheme.color.border.default,
    tickColor: axiomTheme.color.border.default,
    labels: {
      style: {
        color: axiomTheme.color.text.muted,
        fontSize: '11px',
      },
    },
  },
  yAxis: {
    gridLineColor: axiomTheme.color.border.subtle,
    title: {
      style: {
        color: axiomTheme.color.text.muted,
      },
    },
    labels: {
      style: {
        color: axiomTheme.color.text.muted,
        fontSize: '11px',
      },
    },
  },
  legend: {
    itemStyle: {
      color: axiomTheme.color.text.secondary,
      fontWeight: '700',
    },
    itemHoverStyle: {
      color: axiomTheme.color.text.primary,
    },
  },
  tooltip: {
    backgroundColor: 'rgba(15, 23, 42, 0.96)',
    borderColor: axiomTheme.color.border.strong,
    borderRadius: 10,
    style: {
      color: axiomTheme.color.text.primary,
    },
  },
  colors: [
    axiomTheme.color.tone.cyan,
    axiomTheme.color.tone.blue,
    axiomTheme.color.tone.emerald,
    axiomTheme.color.tone.violet,
    axiomTheme.color.tone.amber,
    axiomTheme.color.tone.red,
  ],
};

const mergeOptions = (target: Highcharts.Options, source: Highcharts.Options): Highcharts.Options => ({
  ...target,
  ...source,
  chart: { ...target.chart, ...source.chart },
  title: { ...target.title, ...source.title },
  subtitle: { ...target.subtitle, ...source.subtitle },
  xAxis: source.xAxis || target.xAxis,
  yAxis: source.yAxis || target.yAxis,
  legend: { ...target.legend, ...source.legend },
  tooltip: { ...target.tooltip, ...source.tooltip },
  plotOptions: { ...target.plotOptions, ...source.plotOptions },
});

export const InstitutionalChart: React.FC<InstitutionalChartProps> = ({ options, height = 280 }) => {
  const merged = useMemo(() => mergeOptions(baseOptions, options), [options]);

  return (
    <ChartShell $height={height}>
      <HighchartsReact highcharts={Highcharts} options={merged} containerProps={{ style: { height: '100%' } }} />
    </ChartShell>
  );
};

export default InstitutionalChart;
